import ApiService from './ApiService'
import { getSecureItem, removeSecureItem, setSecureItem, SECURE_AUTH_KEYS } from '@/lib/secureStorage'
import { parsePermissionTree } from '@/lib/parsePermissionTree'
import type {
  PermissionTree,
  RegisterRequest,
  RegistrationResult,
  TokenResponse,
  User,
  UserPermissionsResponse,
} from '@/types/auth'

class AuthService extends ApiService {
  private readonly accessTokenKey = 'accessToken'
  private readonly refreshTokenKey = 'refreshToken'

  isAuthenticated(): boolean {
    const accessToken = this.getAccessToken()
    const refreshToken = getSecureItem(this.refreshTokenKey)

    const accessValid = !!accessToken && !this.isTokenExpired(accessToken)
    const refreshValid = !!refreshToken && !this.isTokenExpired(refreshToken)
    return accessValid || refreshValid
  }

  getRefreshToken(): string | null {
    return getSecureItem(this.refreshTokenKey)
  }

  getAccessToken(): string | null {
    return getSecureItem(this.accessTokenKey)
  }

  isTokenExpired(token: string): boolean {
    try {
      const parts = token.split('.')
      if (parts.length !== 3) {
        return false
      }
      const decoded = JSON.parse(atob(parts[1])) as { exp?: number }
      if (typeof decoded.exp !== 'number') {
        return false
      }
      const now = Date.now() / 1000
      const CLOCK_SKEW_SECONDS = 30
      return decoded.exp < now - CLOCK_SKEW_SECONDS
    } catch {
      return true
    }
  }

  async login(email: string, password: string): Promise<TokenResponse> {
    const response = await this.post<TokenResponse>(
      '/api/auth/login',
      { email, password },
      { requiresAuth: false },
    )

    if (!response.accessToken || !response.refreshToken) {
      throw new Error('Invalid response from server')
    }

    setSecureItem(this.accessTokenKey, response.accessToken)
    setSecureItem(this.refreshTokenKey, response.refreshToken)
    setSecureItem('userEmail', email)
    window.dispatchEvent(new Event('authChange'))

    return {
      ...response,
      permissions: parsePermissionTree(response.permissions),
    }
  }

  async register(data: RegisterRequest): Promise<RegistrationResult> {
    return this.post<RegistrationResult>('/api/auth/register', data, { requiresAuth: false })
  }

  async verifyEmail(token: string): Promise<void> {
    await this.post('/api/auth/verify-email', { token }, { requiresAuth: false })
  }

  async forgotPassword(email: string): Promise<void> {
    await this.post('/api/auth/forgot-password', { email }, { requiresAuth: false })
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await this.post('/api/auth/reset-password', { token, newPassword }, { requiresAuth: false })
  }

  /** Public — completes an email invite by setting the invited user's password. */
  async acceptInvite(token: string, password: string): Promise<void> {
    await this.post('/api/auth/accept-invite', { token, password }, { requiresAuth: false })
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.post('/api/auth/change-password', { currentPassword, newPassword })
  }

  async getCurrentUser(): Promise<User> {
    return this.get<User>('/api/auth/user')
  }

  async getUserPermissions(userId: string): Promise<UserPermissionsResponse> {
    const response = await this.get<UserPermissionsResponse>(`/api/auth/user/${userId}/permissions`)
    return {
      ...response,
      permissions: parsePermissionTree(response.permissions),
    }
  }

  async getOrgPermissions(): Promise<PermissionTree> {
    const response = await this.get<PermissionTree | string>('/api/auth/permissions/org')
    return parsePermissionTree(response)
  }

  /**
   * Every module/model/action that exists in the system, as a nested boolean tree (same shape
   * as /api/auth/permissions/org) — NOT a flat list of ids. It cannot be used directly to build
   * a permission picker; use `usePermissionCatalog`, which sources real permission ids from
   * existing roles instead.
   */
  async getPermissionsCatalogTree(): Promise<PermissionTree> {
    const response = await this.get<PermissionTree | string>('/api/auth/permissions/catalog')
    return parsePermissionTree(response)
  }

  async logout(): Promise<void> {
    const refreshToken = getSecureItem(this.refreshTokenKey)
    if (refreshToken) {
      try {
        await this.post('/api/auth/logout', { refreshToken }, { requiresAuth: false })
      } catch (error) {
        console.error('Logout API error:', error)
      }
    }

    for (const key of SECURE_AUTH_KEYS) removeSecureItem(key)

    window.dispatchEvent(new Event('authChange'))
    window.location.href = '/auth/'
  }
}

export default AuthService
